import AsyncSelect from 'react-select/async'
import { useEffect, useState } from 'react'
import Element, { BaseProps } from './Element'
import axios from 'axios'
import { ActionMeta, MultiValue, SingleValue } from 'react-select'

export interface AutocompleteOption {
  readonly value: string
  readonly label: string
}

type AutocompleteSelectProps = BaseProps & {
  name: string
  onChange: Function
  multiple?: boolean
  autoCompleteToken?: string
  autoCompleteTokenUrl?: string
  defaultValue?: string | number | string[] | number[]
  isDisabled?: boolean
  styles?: any
  placeholder?: string
}

function AutocompleteSelect(props: AutocompleteSelectProps) {
  const [autoCompleteToken, setAutoCompleteToken] = useState('')
  const [value, setValue] = useState<
    SingleValue<AutocompleteOption> | MultiValue<AutocompleteOption>
  >(null)

  useEffect(() => {
    if (props.autoCompleteToken) {
      setAutoCompleteToken(props.autoCompleteToken)
    } else if (!autoCompleteToken && props.autoCompleteTokenUrl) {
      // Keep session cookie aligned with ResultViewer list requests that create tokens.
      axios
        .get(props.autoCompleteTokenUrl, { withCredentials: true })
        .then((response: any) => {
          const token = response?.data?.data
          setAutoCompleteToken(typeof token === 'string' ? token : '')
        })
        .catch(() => {
          setAutoCompleteToken('')
        })
    }
  }, [props.autoCompleteTokenUrl, autoCompleteToken, props.autoCompleteToken])

  const promiseOptions = (inputValue: string) =>
    new Promise<AutocompleteOption[]>((resolve) => {
      if (!autoCompleteToken) {
        resolve([])
        return
      }

      let postData = {
        token: autoCompleteToken,
        term: inputValue,
        id: null as any,
      }
      if (inputValue === '' && props.defaultValue) {
        postData.id = props.defaultValue as any
      }

      // withCredentials is required so PHP uses the same session that stored the token.
      // ResultViewer list/filter calls create autocomplete tokens with credentials;
      // without the cookie, bootstrap falls back to a JWT-derived session and the token misses.
      axios
        .post('/autocomplete', postData, { withCredentials: true })
        .then((response) => {
          // Global axios interceptor resolves some errors instead of rejecting.
          if (!response?.data || (response as any).isAxiosError) {
            resolve([])
            return
          }

          let options: AutocompleteOption[] = []
          if (response.data?.data) {
            for (let item of response.data.data) {
              options.push({
                value: item.id,
                label: item.text,
              })
            }
          }
          if (
            inputValue === '' &&
            props.defaultValue &&
            (props.defaultValue as Array<any>).length !== 0
          ) {
            if (props.multiple) {
              setValue(options)
            } else {
              setValue(options[0])
            }
          }
          resolve(options)
        })
        .catch(() => {
          resolve([])
        })
    })

  return (
    <Element title={props.title} required={props.required}>
      {autoCompleteToken && props.defaultValue !== undefined ? (
        <div
          style={{ width: '100%' }}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
        >
          <AsyncSelect
            cacheOptions
            defaultOptions
            className='w-100'
            loadOptions={promiseOptions}
            value={value}
            isClearable={!props.required}
            isDisabled={props.isDisabled ?? false}
            isMulti={props.multiple}
            onChange={(
              option: SingleValue<AutocompleteOption> | MultiValue<AutocompleteOption>,
              meta: ActionMeta<AutocompleteOption>
            ) => {
              if (meta.action === 'clear') {
                setValue(null)
                let event = {
                  target: {
                    name: props.name,
                    value: null,
                  },
                }
                props.onChange(event)
              } else {
                setValue(option)
                let data = option as any
                if (props.multiple) {
                  let values = data.map((item: AutocompleteOption) => {
                    return item.value
                  })
                  let event = {
                    target: {
                      name: props.name,
                      value: values,
                    },
                  }
                  props.onChange(event)
                } else {
                  let event = {
                    target: {
                      name: props.name,
                      value: data.value,
                    },
                  }
                  props.onChange(event)
                }
              }
            }}
            styles={props.styles}
          />
        </div>
      ) : (
        <></>
      )}
    </Element>
  )
}

export default AutocompleteSelect
